import { useTranslation } from 'react-i18next';
import { Map, ControlPosition } from 'shared/map-exports';
import { ButtonBase, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Compass from 'assets/compass.svg?react';
import { getDirectionFromBearing } from 'features/mapping/mapping.utils';
import { useMapGLController } from 'features/mapping/hooks/useMapGLController/useMapGLController';
import { microCapsLabelSx } from '../MapControls.constants';
import {
  BaseMapboxGLController,
  BaseMapboxGLControllerProps,
} from '../BaseMapboxGLController';

type CompassControlProps = {
  position?: ControlPosition | undefined;
  resetMapBearing: () => Promise<void>;
  map?: Map;
};

type RotateProps = {
  deg: number;
};
const useStyles = makeStyles<RotateProps>()((theme, props) => {
  const { spacing, palette } = theme;

  return {
    compassArrow: {
      rotate: `${0 - props.deg}deg`,
      width: `${spacing(4)} !important`,
      height: `${spacing(4)} !important`,
      color: palette.text.primary,
      position: 'absolute',
    },
    compassButton: {
      width: `${spacing(4)} !important`,
      height: `${spacing(4)} !important`,
      display: 'flex !important',
      flexDirection: 'column',
      backgroundColor: palette.button.surface.dark,
      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
      borderRadius: `${spacing(5)}`,
      marginTop: '16px',
      margin: '16px 16px 0 16px',
      pointerEvents: 'auto',
    },
    directionText: {
      color: palette.text.secondary,
      margin: 0,
    },
  };
});

const CompassControlContent = (props: CompassControlProps): JSX.Element => {
  const { map, resetMapBearing } = props;
  const { t } = useTranslation();

  const deg = map ? map.getBearing() : 0;
  const { classes } = useStyles({ deg });
  const dir = getDirectionFromBearing(deg);

  return (
    <ButtonBase
      className={`${classes.compassButton}`}
      aria-label={t('map.controls.compass')}
      onClick={() => resetMapBearing()}
    >
      <Compass className={classes.compassArrow} />
      <Typography
        className={classes.directionText}
        component="span"
        sx={microCapsLabelSx}
      >
        {dir}
      </Typography>
    </ButtonBase>
  );
};

class MapboxGLCompassController extends BaseMapboxGLController<
  CompassControlProps & BaseMapboxGLControllerProps
> {
  content = (): JSX.Element => (
    <CompassControlContent map={this.map} {...this.props} />
  );
}

export const CompassControl = (props: CompassControlProps): null => {
  useMapGLController(props, MapboxGLCompassController);
  return null;
};
